/**
 * Single source of truth for spray session status display.
 *
 * Shared between SpraySessionList (admin), SpraySessionForm (admin edit),
 * SpraySessionDetail (admin detail) and OperatorSessionList (Field Mode)
 * so a status flip in one screen is visually consistent everywhere.
 *
 * Track: spray_session_workflow_20260427 / Phase 3 / Task 6.
 */

/**
 * Bootstrap badge background variant per session status.
 *   draft       → secondary (grey)   — setup not yet complete
 *   planned     → warning   (amber)  — ready, not yet started
 *   in_progress → primary   (blue)   — live work; reads at a glance
 *   completed   → success   (green)
 *   cancelled   → danger    (red)
 *   deleted     → secondary (grey)
 */
export const STATUS_VARIANT: Record<string, string> = {
  draft: 'secondary',
  planned: 'warning',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'danger',
  deleted: 'secondary',
};

export const STATUS_I18N_KEY: Record<string, string> = {
  draft: 'spray.sessions.statusDraft',
  planned: 'spray.sessions.statusPlanned',
  in_progress: 'spray.sessions.statusInProgress',
  completed: 'spray.sessions.statusCompleted',
  cancelled: 'spray.sessions.statusCancelled',
  deleted: 'spray.sessions.statusDeleted',
};

export const getStatusVariant = (status: string | undefined | null): string =>
  (status && STATUS_VARIANT[status]) || 'secondary';

export const getStatusI18nKey = (status: string | undefined | null): string =>
  (status && STATUS_I18N_KEY[status]) || 'spray.sessions.status';

/**
 * Statuses that represent a fully-closed session (no further transitions
 * possible). Used to gate the "Cancel session" action in both the form
 * and detail views.
 */
export const TERMINAL_STATUSES: ReadonlyArray<string> = ['cancelled', 'completed', 'deleted'];

/**
 * Statuses that lock name / PPE editing and trigger read-only form
 * rendering for non-admins. Superset of TERMINAL_STATUSES — also
 * includes in_progress (a live session the operator has started).
 */
export const TERMINAL_ISH_STATUSES: ReadonlyArray<string> = ['completed', 'cancelled', 'deleted', 'in_progress'];
